import {
  AdMob,
  AdmobConsentStatus,
  type RewardInterstitialAdOptions,
  type RewardAdOptions,
} from '@capacitor-community/admob'
import { defineStore, storeToRefs } from 'pinia'
import { watch, ref } from 'vue'
import { useAuthStore } from '@/stores/auth.ts'
import axios from 'axios'
import { showToast } from '@/utils/showToast'
import { useLocalizationStore } from '@/stores/localization.ts'
import { usePreferencesStore } from '@/stores/preferences.ts'
import { useUiStore } from '@/stores/ui.store.ts'
import { usePaymentStore } from '@/stores/payment.ts'

export const useAdmobStore = defineStore('admobStore', () => {
  const isAdLoading = ref(false)
  const trackingAuthorized = ref(false)
  const config = ref({
    adSkipCost: 2,
    shortAdReward: 1,
    longAdReward: 4,
    adScoreIncrease: 0,
  })
  const { setCurrentAdScore } = usePreferencesStore()
  const { openAdConsent } = useUiStore()
  const { currentAdScore } = storeToRefs(usePreferencesStore())
  const { authState } = storeToRefs(useAuthStore())
  const { t } = useLocalizationStore()
  const adSkipTokens = ref(0)
  const { customerInfo } = storeToRefs(usePaymentStore())
  const httpApi = axios.create({ baseURL: `${import.meta.env.VITE_AUTH_API_DOMAIN}/api` })

  watch(
    authState,
    () => {
      adSkipTokens.value = authState.value.userAdmin?.adSkipTokens || 0
    },
    { deep: true, immediate: true },
  )

  async function increaseAdScore(increase: number = config.value.adScoreIncrease): Promise<void> {
    if (
      customerInfo.value?.activeSubscriptions &&
      customerInfo.value.activeSubscriptions.length > 0
    ) {
      return
    }
    const newAdScore = currentAdScore.value + increase
    if (newAdScore >= 100) {
      openAdConsent()
    } else {
      await setCurrentAdScore(newAdScore)
    }
  }

  async function showTrackingConsentForm(isInit: boolean = false): Promise<void> {
    const consentInfo = await AdMob.requestConsentInfo()

    if (consentInfo.isConsentFormAvailable && consentInfo.status === AdmobConsentStatus.REQUIRED) {
      await AdMob.showConsentForm()
    } else if (
      consentInfo.isConsentFormAvailable &&
      consentInfo.status === AdmobConsentStatus.OBTAINED &&
      !isInit
    ) {
      await AdMob.showPrivacyOptionsForm()
    }

    const trackingInfo = await AdMob.trackingAuthorizationStatus()
    if (trackingInfo.status === 'notDetermined') {
      await AdMob.requestTrackingAuthorization()
    }
    const finalStatus = await AdMob.trackingAuthorizationStatus()
    trackingAuthorized.value = finalStatus.status === 'authorized'
  }

  async function init() {
    await AdMob.initialize()
    await showTrackingConsentForm(true)
  }

  async function showRewardedAd() {
    if (isAdLoading.value || !authState.value.userAdmin?.userId) {
      console.warn('Ad is already loading or user is not defined, skipping ad.')
      return
    }
    const options: RewardAdOptions = {
      adId: import.meta.env.VITE_REWARDED_AD_ID,
      npa: !trackingAuthorized.value,
      ssv: {
        userId: authState.value.userAdmin.userId,
      },
    }
    isAdLoading.value = true
    try {
      await AdMob.prepareRewardVideoAd(options)

      const reward = await AdMob.showRewardVideoAd()
      if (reward) {
        adSkipTokens.value += reward.amount
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('No fill')) {
        showToast({
          text: t('admobNoFillLucky'),
        })
      } else {
        showToast({
          text: `Error showing long ad: ${error}`,
          duration: 'long',
        })
      }
    } finally {
      isAdLoading.value = false
      await setCurrentAdScore(0)
    }
  }

  async function showRewardedInterstitial() {
    if (isAdLoading.value || !authState.value.userAdmin?.userId) {
      console.warn('Ad is already loading or user is not defined, skipping ad.')
      return
    }
    const options: RewardInterstitialAdOptions = {
      adId: import.meta.env.VITE_INTERSTITIAL_AD_ID,
      npa: !trackingAuthorized.value,
      ssv: {
        userId: authState.value.userAdmin.userId,
      },
    }
    isAdLoading.value = true
    try {
      await AdMob.prepareRewardInterstitialAd(options)
      const reward = await AdMob.showRewardInterstitialAd()
      if (reward) {
        adSkipTokens.value += reward.amount
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('No fill')) {
        showToast({
          text: t('admobNoFillLucky'),
        })
      } else {
        showToast({
          text: `Error showing short ad: ${error}`,
          duration: 'long',
        })
      }
    } finally {
      isAdLoading.value = false
      await setCurrentAdScore(0)
    }
  }

  async function useAdSkipTokens(): Promise<boolean> {
    try {
      const response = await httpApi.post('useAdToken')
      if (response.status !== 200) {
        showToast({
          text: t('admobNoFillLucky'),
        })
        return false
      }
      adSkipTokens.value -= config.value.adSkipCost
      return true
    } catch (error) {
      if (error instanceof Error && error.message.includes('No fill')) {
        showToast({
          text: `Error using ad tokens ${error}`,
        })
      } else {
        showToast({
          text: `Error using ad tokens ${error}`,
        })
      }
      return false
    } finally {
      await setCurrentAdScore(0)
    }
  }

  return {
    init,
    isAdLoading,
    config,
    adSkipTokens,
    useAdSkipTokens,
    showRewardedAd,
    showRewardedInterstitial,
    showTrackingConsentForm,
    increaseAdScore,
    setCurrentAdScore,
  }
})
